import React, { useState } from 'react';
import { useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { ConnectionHandler } from 'relay-runtime';
import { css } from 'emotion';
import { GrantPrivacyAuthorizationMutation } from './__generated__/GrantPrivacyAuthorizationMutation.graphql';

const DEFAULT_LIFETIME_DAYS = 180;

const row = css`
    display: flex;
    gap: 0.5em;
    align-items: center;
    flex-wrap: wrap;
`
const reasonField = css`
    flex: 1 1 24em;
    padding: 0.4em;
`
const daysField = css`
    width: 4.5em;
    padding: 0.4em;
`
const problem = css`
    color: #a11111;
    margin-top: 0.4em;
`

/**
 * Records a rider's authorization on their behalf.
 *
 * The rider agrees somewhere else entirely -- a support conversation, an
 * email -- so the reason is required and stored: it is the only trace of why
 * whoever clicked believed they had that agreement.
 */
export default function GrantPrivacyAuthorization({ appuserId }: { appuserId: string }) {
    const [reason, setReason] = useState('');
    const [days, setDays] = useState(String(DEFAULT_LIFETIME_DAYS));
    const [error, setError] = useState<string | null>(null);

    const [grant, isGranting] = useMutation<GrantPrivacyAuthorizationMutation>(graphql`
        mutation GrantPrivacyAuthorizationMutation(
            $appuserId: ID!
            $reason: String!
            $lifetimeDays: Int
        ) {
            createPrivacyAuthorization(
                appuserId: $appuserId
                reason: $reason
                lifetimeDays: $lifetimeDays
            ) {
                errorMessage
                authorization {
                    id
                    historyStart
                    historyEnd
                    notValidAfter
                    reason
                    grantedBy
                }
            }
        }
    `);

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        grant({
            variables: { appuserId, reason, lifetimeDays: Number(days) },
            // Put the new authorization into the list beside us. Without this
            // the grant succeeds invisibly, and the obvious response to that
            // is to click again.
            updater: (store) => {
                const authorization = store
                    .getRootField('createPrivacyAuthorization')
                    ?.getLinkedRecord('authorization');
                const appuser = store.get(appuserId);
                if (!authorization || !appuser) {
                    return;
                }
                const connection = ConnectionHandler.getConnection(
                    appuser,
                    'PrivacyAuthorizationList_privacyAuthorizations',
                );
                if (!connection) {
                    return;
                }
                const edge = ConnectionHandler.createEdge(
                    store,
                    connection,
                    authorization,
                    'AppDataPrivacyAuthorizationNodeEdge',
                );
                ConnectionHandler.insertEdgeAfter(connection, edge);
            },
            onCompleted: (response) => {
                const message = response.createPrivacyAuthorization?.errorMessage;
                if (message) {
                    setError(message);
                } else {
                    setReason('');
                }
            },
            onError: (err) => setError(err.message),
        });
    };

    return (
        <form onSubmit={submit}>
            <div className={row}>
                <input
                    className={reasonField}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why, and where they agreed (e.g. MA-1234, zendesk 5678)"
                    aria-label="Reason for granting this authorization"
                />
                <label>
                    for{' '}
                    <input
                        className={daysField}
                        type="number"
                        min={1}
                        max={365}
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                        aria-label="Days this authorization stays valid"
                    />{' '}
                    days
                </label>
                <button type="submit" disabled={!reason.trim() || isGranting}>
                    {isGranting ? 'Granting…' : 'Grant authorization'}
                </button>
            </div>
            {error && <div className={problem}>{error}</div>}
        </form>
    );
}
